import { useCallback, useEffect, useMemo, useState } from "react";
import { LockedAutocomplete } from "@/components/LockedAutocomplete";
import type { AutocompleteOption } from "@/components/LockedAutocomplete";
import {
  applyDevRoleGroups,
  applyDevRoleImpersonation,
  applyDevSimulateBirthday,
  applyDevSimulateChristmas,
  applyDevSimulateHalloween,
  applyDevSimulateVacation,
  clearDevRoleSwitch,
  fetchDevRoleCatalog,
  fetchDevRoleSwitchState,
  fetchDevRoleUserGroups,
  searchDevRoleEmployees,
  type DevRoleCatalog,
  type DevRoleEmployeeSearchHit,
  type DevRoleSwitchState,
} from "@/lib/devRoleSwitch";

function sortedUnique(groups: string[]): string[] {
  return [...new Set(groups.map((g) => g.trim()).filter(Boolean))].sort((a, b) =>
    a.localeCompare(b, "de"),
  );
}

/** Nur simulierte Rollen senden – Dev-Zugang und echte Admin-Rollen bleiben serverseitig. */
function groupsForApply(
  selected: Iterable<string>,
  preserve: string[],
  realGroups: string[] | undefined,
): string[] {
  const preserveKeys = new Set(preserve.map((g) => g.toLowerCase()));
  const realKeys = new Set(
    (realGroups || []).map((g) => g.trim().toLowerCase()).filter(Boolean),
  );
  return [...selected].filter((g) => {
    const key = g.trim().toLowerCase();
    return key && !preserveKeys.has(key) && !realKeys.has(key);
  });
}

type EmployeePick = DevRoleEmployeeSearchHit;

interface DevRoleSwitcherPanelProps {
  onApplied?: () => void;
}

export function DevRoleSwitcherPanel({ onApplied }: DevRoleSwitcherPanelProps) {
  const [state, setState] = useState<DevRoleSwitchState | null>(null);
  const [catalog, setCatalog] = useState<DevRoleCatalog | null>(null);
  const [apiError, setApiError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [employeePick, setEmployeePick] = useState<AutocompleteOption<EmployeePick> | null>(
    null,
  );
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [simulateBirthday, setSimulateBirthday] = useState(false);
  const [simulateChristmas, setSimulateChristmas] = useState(false);
  const [simulateHalloween, setSimulateHalloween] = useState(false);
  const [simulateVacation, setSimulateVacation] = useState(false);

  const load = useCallback(async () => {
    const result = await fetchDevRoleSwitchState();
    if (!result.ok) {
      setState(null);
      setCatalog(null);
      setApiError(result.message);
      return;
    }
    setState(result.state);
    setApiError(null);
    setSimulateBirthday(!!result.state.simulate_birthday);
    setSimulateChristmas(!!result.state.simulate_christmas);
    setSimulateHalloween(!!result.state.simulate_halloween);
    setSimulateVacation(!!result.state.simulate_vacation);
    const preserveKeys = new Set(
      (result.state.dev_preserve_groups || ["App-Admins", "App-Tester"]).map((g) =>
        g.toLowerCase(),
      ),
    );
    const realKeys = new Set((result.state.groups_real || []).map((g) => g.toLowerCase()));
    setSelected(
      new Set(
        (result.state.active ? result.state.groups : []).filter((g) => {
          const key = g.toLowerCase();
          return key && !preserveKeys.has(key) && !realKeys.has(key);
        }),
      ),
    );
    if (result.state.impersonate_login) {
      setSelectedEmployee(result.state.impersonate_login);
    }
    try {
      const cat = await fetchDevRoleCatalog();
      setCatalog(cat);
    } catch {
      setCatalog({ employees: [], groups: [], employee_count: 0, dev_preserve_groups: [] });
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const preserveLabel = (catalog?.dev_preserve_groups || state?.dev_preserve_groups || [
    "App-Admins",
    "App-Tester",
  ]).join(", ");

  const available = useMemo(() => {
    if (!state) return [];
    return sortedUnique([
      ...(catalog?.groups || []),
      ...state.available_groups,
      ...state.groups,
    ]);
  }, [state, catalog]);

  const loadEmployeeOptions = useCallback(
    async (query: string): Promise<AutocompleteOption<EmployeePick>[]> => {
      const rows = await searchDevRoleEmployees(query, 50);
      return rows.map((row) => ({
        key: row.login,
        label: row.label || row.login,
        description: row.subtitle ? `${row.subtitle} · @${row.login}` : `@${row.login}`,
        value: row,
      }));
    },
    [],
  );

  async function onEmployeeChange(next: AutocompleteOption<EmployeePick> | null) {
    setEmployeePick(next);
    if (!next) {
      setSelectedEmployee("");
      return;
    }
    const login = next.value.login;
    setSelectedEmployee(login);
    setGroupsLoading(true);
    try {
      const groups = await fetchDevRoleUserGroups(login, next.value.email || next.value.subtitle);
      setSelected(new Set(groups));
    } catch {
      setSelected(new Set(next.value.groups || []));
      setApiError(
        "AD-Gruppen für diesen Login konnten nicht geladen werden – Gruppen manuell wählen oder „Rolle übernehmen“.",
      );
    } finally {
      setGroupsLoading(false);
    }
  }

  function toggle(group: string, checked: boolean) {
    setSelectedEmployee("");
    setEmployeePick(null);
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) next.add(group);
      else next.delete(group);
      return next;
    });
  }

  function applyPreset(groups: string[]) {
    setSelectedEmployee("");
    setEmployeePick(null);
    setSelected(new Set(groups));
  }

  async function finish(fn: () => Promise<void>) {
    setBusy(true);
    try {
      await fn();
      onApplied?.();
      window.location.reload();
    } catch (err) {
      setBusy(false);
      setApiError(err instanceof Error ? err.message : "Fehler");
    }
  }

  if (apiError && !state) {
    return <p className="text-sm text-amber-700 dark:text-amber-300">{apiError}</p>;
  }

  if (!state || !catalog) {
    return <p className="text-sm text-label-tertiary">Lade Katalog …</p>;
  }

  const picked = [...selected];

  return (
    <div className="space-y-3">
      <p className="text-xs leading-relaxed text-label-secondary">
        In der Simulation siehst du die App wie der gewählte Benutzer. Nur für den Dev-Zugang
        und den Rollenwechsler bleiben intern aktiv: {preserveLabel} – deine übrigen Rollen
        (z. B. Büro) werden nicht mitgeschleppt.
      </p>

      <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2.5 text-sm text-label">
        <input
          type="checkbox"
          className="h-4 w-4 shrink-0 accent-amber-600"
          checked={simulateBirthday}
          disabled={busy}
          onChange={(e) => void finish(() => applyDevSimulateBirthday(e.target.checked))}
        />
        <span>
          <span className="font-medium">Meinen Geburtstag simulieren</span>
          <span className="mt-0.5 block text-xs text-label-secondary">
            Konfetti, Geburtstags-Banner und Konto-Badge – auch im Dezember statt Schnee.
          </span>
        </span>
      </label>

      <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-emerald-600/35 bg-emerald-500/10 px-3 py-2.5 text-sm text-label">
        <input
          type="checkbox"
          className="h-4 w-4 shrink-0 accent-emerald-700"
          checked={simulateChristmas}
          disabled={busy}
          onChange={(e) => void finish(() => applyDevSimulateChristmas(e.target.checked))}
        />
        <span>
          <span className="font-medium">Weihnachten simulieren</span>
          <span className="mt-0.5 block text-xs text-label-secondary">
            Schneefall und dezente Festtags-Dekoration – an jedem Dezembertag (außer am eigenen Geburtstag).
          </span>
        </span>
      </label>

      <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-orange-500/45 bg-orange-500/10 px-3 py-2.5 text-sm text-label">
        <input
          type="checkbox"
          className="h-4 w-4 shrink-0 accent-orange-600"
          checked={simulateHalloween}
          disabled={busy}
          onChange={(e) => void finish(() => applyDevSimulateHalloween(e.target.checked))}
        />
        <span>
          <span className="font-medium">Halloween simulieren</span>
          <span className="mt-0.5 block text-xs text-label-secondary">
            Fledermäuse, Nebel und Kürbis-Dekoration – wie vom 24. bis 31. Oktober (hat Vorrang vor Weihnachten).
          </span>
        </span>
      </label>

      <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-sky-500/40 bg-sky-500/10 px-3 py-2.5 text-sm text-label">
        <input
          type="checkbox"
          className="h-4 w-4 shrink-0 accent-sky-600"
          checked={simulateVacation}
          disabled={busy}
          onChange={(e) => void finish(() => applyDevSimulateVacation(e.target.checked))}
        />
        <span>
          <span className="font-medium">Urlaub simulieren</span>
          <span className="mt-0.5 block text-xs text-label-secondary">
            Dezente Urlaubs-Dekoration und ein persönlicher Urlaubswunsch – unabhängig von echten
            Abwesenheiten.
          </span>
        </span>
      </label>

      <div className="space-y-2">
        <LockedAutocomplete<EmployeePick>
          label="Mitarbeiter"
          placeholder="Name oder E-Mail suchen …"
          emptyText="Kein Mitarbeiter gefunden."
          minChars={0}
          debounceMs={200}
          value={employeePick}
          disabled={busy || groupsLoading}
          loadOptions={loadEmployeeOptions}
          onChange={(next) => void onEmployeeChange(next)}
        />
        <button
          type="button"
          disabled={busy || !selectedEmployee || groupsLoading}
          className="w-full rounded-lg bg-amber-600 px-3 py-2 text-xs font-semibold text-white hover:bg-amber-700 disabled:opacity-50"
          onClick={() => void finish(() => applyDevRoleImpersonation(selectedEmployee))}
        >
          Rolle dieses Mitarbeiters übernehmen
        </button>
      </div>

      <div className="flex flex-wrap gap-1.5">
        {state.presets.map((p) => (
          <button
            key={p.id}
            type="button"
            disabled={busy}
            className="rounded-full border border-amber-500/45 bg-amber-500/15 px-2 py-0.5 text-[11px] font-semibold text-amber-900 hover:bg-amber-500/25 dark:text-amber-200"
            title={p.groups.join(", ")}
            onClick={() => applyPreset(p.groups)}
          >
            {p.label}
          </button>
        ))}
      </div>

      <div
        className="max-h-[min(16rem,42vh)] overflow-y-auto rounded-lg border border-amber-500/40 bg-card shadow-inner"
        role="group"
        aria-label="Gruppen auswählen"
      >
        {available.map((group) => (
          <label
            key={group}
            className="flex cursor-pointer items-center gap-2 border-b border-separator/50 px-3 py-2.5 text-sm text-label last:border-b-0 hover:bg-amber-500/10"
          >
            <input
              type="checkbox"
              className="h-4 w-4 shrink-0 accent-amber-600"
              checked={selected.has(group)}
              disabled={busy || groupsLoading}
              onChange={(e) => toggle(group, e.target.checked)}
            />
            <span>{group}</span>
          </label>
        ))}
      </div>

      <p className="text-xs font-medium text-amber-700 dark:text-amber-300">
        {groupsLoading
          ? "Gruppen werden geladen …"
          : picked.length
            ? `Auswahl (${picked.length}): ${picked.join(", ")}`
            : "Gruppen wählen oder Mitarbeiter-Rolle übernehmen."}
        {selectedEmployee ? ` · Mitarbeiter: ${selectedEmployee}` : ""}
      </p>

      <div className="flex flex-wrap justify-end gap-2 border-t border-separator pt-3">
        <button
          type="button"
          disabled={busy}
          className="rounded-lg border border-separator bg-fill px-3 py-1.5 text-xs font-semibold text-label hover:bg-fill-tertiary"
          onClick={() => {
            setSelected(new Set());
            setSelectedEmployee("");
            setEmployeePick(null);
          }}
        >
          Auswahl leeren
        </button>
        <button
          type="button"
          disabled={busy}
          className="rounded-lg border border-separator bg-fill px-3 py-1.5 text-xs font-semibold text-label hover:bg-fill-tertiary"
          onClick={() => void finish(clearDevRoleSwitch)}
        >
          Echte Gruppen
        </button>
        <button
          type="button"
          disabled={busy || selected.size === 0}
          className="rounded-lg border border-amber-600/50 bg-amber-500/15 px-3 py-1.5 text-xs font-semibold text-label hover:bg-amber-500/25 disabled:opacity-50"
          onClick={() =>
            void finish(() =>
              applyDevRoleGroups(
                groupsForApply(selected, catalog?.dev_preserve_groups || [], state.groups_real),
              ),
            )
          }
        >
          Gruppen übernehmen
        </button>
      </div>

      {apiError ? <p className="text-xs text-red-600">{apiError}</p> : null}
    </div>
  );
}
